import React, { useLayoutEffect, useRef, useState } from 'react'
import { THEME } from '../../constant/color';

export const CATEGORY_TOTAL_LABELS = ["TOTAL ON-STREET", "TOTAL OFF-STREET", "TOTAL REAL ESTATE ALL", "TOTAL AIRPORTS"];
const SUB_TOTAL_LABELS = [];

export const PAGE_BREAK_AFTER_LABEL = "TOTAL OFF-STREET";

// Font ke liye upper limit. Ye sirf WIDTH ki wajah se hai (16 columns fixed layout
// me hain, font zyada bara hua to numbers right side se cut ho jate hain).
// Cut ho rahe hon to ghata do (1.1 / 1.0), nahi ho rahe aur bara font chahiye to barha do (1.3).
// Height ka masla ab row padding sambhalti hai, is liye is limit ka height se koi talluq nahi.
const MAX_SCALE = 1.2;

const Mawgif_Group_Table_Component = ({ page = null, rows = [], headerYears = [], headerMonths = [], headerMeasures = [], subtitleSuffix = "", maxRows = 0 }) => {
  const columns = headerYears.map((yr, idx) => ({
    year: yr,
    period: headerMonths[idx],
    type: headerMeasures[idx],
  }));

  const tableRows = rows.map((r) => ({
    label: r.label,
    isCategoryTotal: CATEGORY_TOTAL_LABELS.includes(r.label),
    isSubTotal: SUB_TOTAL_LABELS.includes(r.label),
    values: r.values || [],
  }));

  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const theadRef = useRef(null);
  const tbodyRef = useRef(null);

  // scale = font/border multiplier, pad = extra vertical padding (px) per body-row side
  const [fit, setFit] = useState({ scale: 1, pad: 0 });
  const { scale, pad } = fit;

  useLayoutEffect(() => {
    const run = () => {
      const outer = outerRef.current;
      const thead = theadRef.current;
      const tbody = tbodyRef.current;
      if (!outer || !thead || !tbody) return;

      const H = outer.offsetHeight;
      if (H <= 0) return;

      setFit((prev) => {
        const n = tableRows.length || 1;

        // Natural (scale = 1, pad = 0) heights alag alag nikalo
        const headNat = thead.offsetHeight / prev.scale;
        const bodyNat = (tbody.offsetHeight - prev.pad * 2 * n) / prev.scale;
        if (!headNat || bodyNat <= 0) return prev;

        // Scale us part ke hisaab se nikalo jisme sabse zyada rows hain (maxRows),
        // taake Part 1 aur Part 2 dono me header/font ka size bilkul same ho.
        const perRow = bodyNat / n;
        const totalRows = Math.max(maxRows || 0, n);
        const naturalForScale = headNat + perRow * totalRows;
        const nextScale = Math.min(MAX_SCALE, (H / naturalForScale) * 0.99);

        // Is part ki apni rows ke baad jo height bachi wo rows ke padding me
        // (chhote part me padding zyada hogi, table phir bhi poori height bharega)
        const used = (headNat + bodyNat) * nextScale;
        const leftover = Math.max(0, H * 0.99 - used);
        const nextPad = leftover / n / 2;

        if (
          Math.abs(nextScale - prev.scale) < 0.005 &&
          Math.abs(nextPad - prev.pad) < 0.05
        ) {
          return prev;
        }
        return { scale: nextScale, pad: nextPad };
      });
    };

    run();

    const ro = new ResizeObserver(run);
    if (outerRef.current) ro.observe(outerRef.current);
    window.addEventListener("resize", run);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", run);
    };
  }, [tableRows.length, maxRows, fit]);

  // Header + borders: sirf scale
  const px = (n) => `${(n * scale).toFixed(2)}px`;
  // Body rows ka vertical padding: scale + bachi hui height ka hissa
  const rowPx = (n) => `${(n * scale + pad).toFixed(2)}px`;
  const border = (n, color) => `${(n * scale).toFixed(2)}px solid ${color}`;

  const headerCellStyle = () => ({
    borderLeft: border(1, "black"),
    borderRight: border(1, "black"),
    borderTop: border(3, "black"),
    borderBottom: border(1, "black"),
  });

  return (
    <div className="w-full h-full flex flex-col gap-1 overflow-hidden">
      <div ref={outerRef} className="flex-1 min-h-0 w-full" style={{ overflow: "hidden" }}>
        <div ref={innerRef}>
          <table
            style={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "collapse",
            }}
          >
            <thead ref={theadRef}>
              <tr>
                <th
                  rowSpan={2}
                  className="text-white"
                  style={{
                    width: "190px",
                    borderLeft: border(2, "black"),
                    borderRight: border(1, "black"),
                    borderTop: border(1, "black"),
                    borderBottom: border(1, "black"),
                    padding: px(4),
                    fontSize: px(24),
                    backgroundColor: THEME?.colors.blue1
                  }}
                >
                  SAR '000
                </th>
                {columns.map((col, idx) => (
                  <th key={`y-${idx}`} className="text-white" style={{ ...headerCellStyle(), borderTop: border(2, "black"), padding: px(3), fontSize: px(24), backgroundColor: THEME?.colors.blue1 }}>
                    {col.year}
                  </th>
                ))}
              </tr>
              <tr>
                {columns.map((col, idx) => (
                  <th key={`p-${idx}`} className="text-white" style={{ ...headerCellStyle(), padding: px(3), fontSize: px(24), backgroundColor: THEME?.colors.blue1 }}>
                    {col.period}
                  </th>
                ))}
              </tr>
              <tr>
                <th
                  className=" text-white"
                  style={{
                    borderLeft: border(2, "black"),
                    borderRight: border(1, "black"),
                    borderTop: border(1, "#bfbfbf"),
                    borderBottom: border(2, "black"),
                    padding: px(4),
                    paddingLeft: px(8),
                    textAlign: "left",
                    fontSize: px(24),
                    fontWeight: 700,
                    backgroundColor: THEME?.colors.blue1
                  }}
                >
                  {page?.subtitleTable}
                </th>
                {columns.map((col, idx) => (
                  <th key={`t-${idx}`} className=" text-white" style={{ ...headerCellStyle(), padding: px(3), fontSize: px(24), backgroundColor: THEME?.colors.blue1 }}>
                    {col.type}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody ref={tbodyRef}>
              {tableRows.map((row, rowIndex) => {
                const isTotalRow = row.isCategoryTotal;
                const isLastRow = rowIndex === tableRows.length - 1;
                const nextIsTotalRow = !!tableRows[rowIndex + 1]?.isCategoryTotal;
                const rowBottomBorder =
                  isTotalRow || nextIsTotalRow || isLastRow ? border(3, "black") : border(1, "#bfbfbf");

                return (
                  <tr key={rowIndex}>
                    <td
                      style={{
                        fontWeight: isTotalRow || row.isSubTotal ? 700 : 400,
                        color: "black",
                        paddingTop: rowPx(2),
                        paddingBottom: rowPx(2),
                        paddingLeft: px(10),
                        paddingRight: px(4),
                        borderLeft: border(2, "black"),
                        borderRight: border(1, "black"),
                        borderBottom: rowBottomBorder,
                        fontSize: px(24),
                        backgroundColor: isTotalRow ? THEME?.colors?.grayMedium : THEME?.colors?.grayLight
                      }}
                    >
                      {row.label}
                    </td>

                    {row.values.map((value, colIndex) => {
                      const isNegative = typeof value === "string" && value.trim().startsWith("(");
                      const isActualOrBudget = ["Actual", "Budget"].includes(columns[colIndex]?.type);
                      const cellStyle = {
                        textAlign: "right",
                        padding: `${rowPx(1)} ${px(4)}`,
                        fontSize: px(24),
                        borderLeft: [0, 4, 8, 12].includes(colIndex) ? border(1, "black") : border(1, "#bfbfbf"),
                        borderRight: colIndex === 15 ? border(2, "black") : [3, 7, 11].includes(colIndex) ? border(1, "black") : border(1, "#bfbfbf"),
                        borderBottom: rowBottomBorder,
                        color: isNegative ? THEME?.colors?.red : "black",
                        fontWeight: isTotalRow || row.isSubTotal ? 700 : 400,
                      };

                      return (
                        <td
                          key={colIndex}
                          style={{ ...cellStyle, backgroundColor: isTotalRow ? THEME?.colors?.grayMedium : isActualOrBudget ? "white" : THEME?.colors?.yellowLight }}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Mawgif_Group_Table_Component;